/**
 * NavigatorX Promo Code Manager
 *
 * Full CRUD management for promotional codes:
 * - Create single promo codes with configurable discount types
 * - Bulk generate codes from a template
 * - View all codes with active/inactive filtering
 * - Search codes by code string
 * - Deactivate codes
 * - Track usage statistics per code
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Ticket,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  Copy,
  CheckCircle,
  XCircle,
  Trash2,
  Layers,
  Percent,
  DollarSign,
  Calendar,
  Hash,
  Eye,
  EyeOff,
  Clock,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import {
  createPromoCode,
  getPromoCodes,
  getPromoCodeByCode,
  deactivatePromoCode,
  bulkGeneratePromoCodes,
  type PromoCode
} from '../../../utils/services/navigatorXBillingService';

interface PromoCodeManagerProps {
  embedded?: boolean;
}

type ViewMode = 'list' | 'create' | 'bulk';
type DiscountType = PromoCode['discountType'];
type FilterMode = 'all' | 'active' | 'inactive' | 'expired';

function getDiscountLabel(code: PromoCode): string {
  switch (code.discountType) {
    case 'percentage':
      return `${code.discountValue}% off`;
    case 'fixed':
      return `$${code.discountValue.toFixed(2)} off`;
    case 'free_months':
      return `${code.discountValue} free month${code.discountValue !== 1 ? 's' : ''}`;
  }
}

function getDiscountTypeIcon(discountType: DiscountType): typeof Percent {
  switch (discountType) {
    case 'percentage': return Percent;
    case 'fixed': return DollarSign;
    case 'free_months': return Calendar;
  }
}

function getCodeStatus(code: PromoCode): { label: string; color: string } {
  if (!code.active) {
    return { label: 'Deactivated', color: 'bg-gray-100 text-gray-700' };
  }

  const now = new Date();
  const validFrom = code.validFrom instanceof Timestamp ? code.validFrom.toDate() : new Date(code.validFrom as unknown as string);
  const validUntil = code.validUntil instanceof Timestamp ? code.validUntil.toDate() : new Date(code.validUntil as unknown as string);

  if (now < validFrom) {
    return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' };
  }
  if (now > validUntil) {
    return { label: 'Expired', color: 'bg-red-100 text-red-700' };
  }
  if (code.maxUses !== null && code.usedCount >= code.maxUses) {
    return { label: 'Max Uses Reached', color: 'bg-yellow-100 text-yellow-700' };
  }
  return { label: 'Active', color: 'bg-green-100 text-green-700' };
}

function formatTimestamp(timestamp: Timestamp | string): string {
  const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function formatTimestampFull(timestamp: Timestamp | string): string {
  const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function PromoCodeManager({ embedded = false }: PromoCodeManagerProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedCode, setSelectedCode] = useState<PromoCode | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  // Create form state
  const [formCode, setFormCode] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDiscountType, setFormDiscountType] = useState<DiscountType>('percentage');
  const [formDiscountValue, setFormDiscountValue] = useState('');
  const [formMaxUses, setFormMaxUses] = useState('');
  const [formValidFrom, setFormValidFrom] = useState('');
  const [formValidUntil, setFormValidUntil] = useState('');

  // Bulk generate state
  const [bulkCount, setBulkCount] = useState('10');
  const [bulkDescription, setBulkDescription] = useState('');
  const [bulkDiscountType, setBulkDiscountType] = useState<DiscountType>('percentage');
  const [bulkDiscountValue, setBulkDiscountValue] = useState('');
  const [bulkMaxUses, setBulkMaxUses] = useState('1');
  const [bulkValidFrom, setBulkValidFrom] = useState('');
  const [bulkValidUntil, setBulkValidUntil] = useState('');

  const adminId = auth.currentUser?.uid || '';

  const loadPromoCodes = useCallback(async () => {
    setLoading(true);
    try {
      const codes = await getPromoCodes();
      setPromoCodes(codes);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load promo codes';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPromoCodes();
  }, [loadPromoCodes]);

  const resetCreateForm = () => {
    setFormCode('');
    setFormDescription('');
    setFormDiscountType('percentage');
    setFormDiscountValue('');
    setFormMaxUses('');
    setFormValidFrom('');
    setFormValidUntil('');
  };

  const resetBulkForm = () => {
    setBulkCount('10');
    setBulkDescription('');
    setBulkDiscountType('percentage');
    setBulkDiscountValue('');
    setBulkMaxUses('1');
    setBulkValidFrom('');
    setBulkValidUntil('');
  };

  const handleCreateCode = async () => {
    if (!formCode.trim()) {
      toast.error('Promo code is required');
      return;
    }
    if (!formDescription.trim()) {
      toast.error('Description is required');
      return;
    }
    const discountValue = parseFloat(formDiscountValue);
    if (isNaN(discountValue) || discountValue <= 0) {
      toast.error('Discount value must be a positive number');
      return;
    }
    if (formDiscountType === 'percentage' && discountValue > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }
    if (!formValidFrom || !formValidUntil) {
      toast.error('Valid from and valid until dates are required');
      return;
    }
    const validFrom = new Date(formValidFrom);
    const validUntil = new Date(formValidUntil);
    if (validUntil <= validFrom) {
      toast.error('Valid until must be after valid from');
      return;
    }

    const existing = await getPromoCodeByCode(formCode);
    if (existing) {
      toast.error(`Promo code "${formCode.toUpperCase()}" already exists`);
      return;
    }

    setSaving(true);
    try {
      const maxUses = formMaxUses.trim() ? parseInt(formMaxUses, 10) : null;
      if (maxUses !== null && (isNaN(maxUses) || maxUses < 1)) {
        toast.error('Max uses must be a positive integer or left empty for unlimited');
        setSaving(false);
        return;
      }

      await createPromoCode(
        {
          code: formCode.trim(),
          description: formDescription.trim(),
          discountType: formDiscountType,
          discountValue,
          maxUses,
          validFrom: Timestamp.fromDate(validFrom),
          validUntil: Timestamp.fromDate(validUntil),
          active: true,
          createdBy: adminId
        },
        adminId
      );

      toast.success(`Promo code "${formCode.toUpperCase()}" created`);
      resetCreateForm();
      setViewMode('list');
      await loadPromoCodes();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create promo code';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkGenerate = async () => {
    const count = parseInt(bulkCount, 10);
    if (isNaN(count) || count < 1 || count > 500) {
      toast.error('Count must be between 1 and 500');
      return;
    }
    if (!bulkDescription.trim()) {
      toast.error('Description is required for bulk generation');
      return;
    }
    const discountValue = parseFloat(bulkDiscountValue);
    if (isNaN(discountValue) || discountValue <= 0) {
      toast.error('Discount value must be a positive number');
      return;
    }
    if (bulkDiscountType === 'percentage' && discountValue > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }
    if (!bulkValidFrom || !bulkValidUntil) {
      toast.error('Valid from and valid until dates are required');
      return;
    }
    const validFrom = new Date(bulkValidFrom);
    const validUntil = new Date(bulkValidUntil);
    if (validUntil <= validFrom) {
      toast.error('Valid until must be after valid from');
      return;
    }

    setSaving(true);
    try {
      const maxUses = bulkMaxUses.trim() ? parseInt(bulkMaxUses, 10) : null;
      if (maxUses !== null && (isNaN(maxUses) || maxUses < 1)) {
        toast.error('Max uses must be a positive integer or left empty for unlimited');
        setSaving(false);
        return;
      }

      const generated = await bulkGeneratePromoCodes(
        count,
        {
          description: bulkDescription.trim(),
          discountType: bulkDiscountType,
          discountValue,
          maxUses,
          validFrom: Timestamp.fromDate(validFrom),
          validUntil: Timestamp.fromDate(validUntil),
          active: true,
          createdBy: adminId
        },
        adminId
      );

      toast.success(`${generated.length} promo codes generated`);
      resetBulkForm();
      setViewMode('list');
      await loadPromoCodes();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate promo codes';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (code: PromoCode) => {
    try {
      await deactivatePromoCode(code.id, adminId);
      toast.success(`Promo code "${code.code}" deactivated`);
      setPromoCodes(prev => prev.map(c => c.id === code.id ? { ...c, active: false } : c));
      if (selectedCode?.id === code.id) {
        setSelectedCode({ ...code, active: false });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to deactivate promo code';
      toast.error(message);
    }
  };

  const handleCopyCode = (code: PromoCode) => {
    navigator.clipboard.writeText(code.code).then(() => {
      setCopiedCodeId(code.id);
      toast.success(`Copied "${code.code}" to clipboard`);
      setTimeout(() => setCopiedCodeId(null), 2000);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const filteredCodes = promoCodes.filter(code => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesCode = code.code.toLowerCase().includes(query);
      const matchesDescription = code.description.toLowerCase().includes(query);
      if (!matchesCode && !matchesDescription) return false;
    }

    switch (filterMode) {
      case 'active':
        return code.active && getCodeStatus(code).label === 'Active';
      case 'inactive':
        return !code.active;
      case 'expired':
        return getCodeStatus(code).label === 'Expired';
      default:
        return true;
    }
  });

  const stats = {
    total: promoCodes.length,
    active: promoCodes.filter(c => c.active && getCodeStatus(c).label === 'Active').length,
    expired: promoCodes.filter(c => getCodeStatus(c).label === 'Expired').length,
    totalRedemptions: promoCodes.reduce((sum, c) => sum + c.usedCount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">Loading promo codes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Codes</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Ticket className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expired</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Redemptions</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalRedemptions}</p>
              </div>
              <Hash className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
              <div className="relative flex-1 sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search codes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="flex items-center gap-1 border rounded-lg p-1">
                {(['all', 'active', 'inactive', 'expired'] as FilterMode[]).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setFilterMode(mode)}
                    className={`px-3 py-1 text-xs rounded-md transition-colors capitalize ${
                      filterMode === mode
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadPromoCodes} title="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'bulk' ? 'default' : 'outline'}
                onClick={() => {
                  setViewMode(viewMode === 'bulk' ? 'list' : 'bulk');
                  if (viewMode !== 'bulk') resetBulkForm();
                }}
              >
                <Layers className="h-4 w-4 mr-1" />
                Bulk Generate
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'create' ? 'default' : 'outline'}
                onClick={() => {
                  setViewMode(viewMode === 'create' ? 'list' : 'create');
                  if (viewMode !== 'create') resetCreateForm();
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Code
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Form */}
      {viewMode === 'create' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create Promo Code
            </CardTitle>
            <CardDescription>Create a single promotional code with custom settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="promo-code">Promo Code</Label>
                <Input
                  id="promo-code"
                  placeholder="e.g. WELCOME2026"
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.toUpperCase())}
                  className="mt-1 uppercase"
                  maxLength={20}
                />
                <p className="text-xs text-gray-400 mt-1">Auto-uppercased. Max 20 characters.</p>
              </div>

              <div>
                <Label htmlFor="promo-description">Description</Label>
                <Input
                  id="promo-description"
                  placeholder="e.g. Welcome offer for new users"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Discount Type</Label>
                <div className="flex items-center gap-2 mt-1">
                  {(['percentage', 'fixed', 'free_months'] as DiscountType[]).map(type => {
                    const Icon = getDiscountTypeIcon(type);
                    const labels: Record<DiscountType, string> = {
                      percentage: 'Percentage',
                      fixed: 'Fixed Amount',
                      free_months: 'Free Months'
                    };
                    return (
                      <button
                        key={type}
                        onClick={() => setFormDiscountType(type)}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          formDiscountType === type
                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {labels[type]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="promo-value">
                  {formDiscountType === 'percentage' ? 'Percentage (%)' :
                   formDiscountType === 'fixed' ? 'Amount ($)' :
                   'Number of Free Months'}
                </Label>
                <Input
                  id="promo-value"
                  type="number"
                  placeholder={formDiscountType === 'percentage' ? '10' : formDiscountType === 'fixed' ? '5.00' : '1'}
                  value={formDiscountValue}
                  onChange={(e) => setFormDiscountValue(e.target.value)}
                  className="mt-1"
                  min="0"
                  max={formDiscountType === 'percentage' ? '100' : undefined}
                  step={formDiscountType === 'fixed' ? '0.01' : '1'}
                />
              </div>

              <div>
                <Label htmlFor="promo-max-uses">Max Uses (leave empty for unlimited)</Label>
                <Input
                  id="promo-max-uses"
                  type="number"
                  placeholder="Unlimited"
                  value={formMaxUses}
                  onChange={(e) => setFormMaxUses(e.target.value)}
                  className="mt-1"
                  min="1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="promo-valid-from">Valid From</Label>
                  <Input
                    id="promo-valid-from"
                    type="date"
                    value={formValidFrom}
                    onChange={(e) => setFormValidFrom(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="promo-valid-until">Valid Until</Label>
                  <Input
                    id="promo-valid-until"
                    type="date"
                    value={formValidUntil}
                    onChange={(e) => setFormValidUntil(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6">
              <Button onClick={handleCreateCode} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                {saving ? 'Creating...' : 'Create Promo Code'}
              </Button>
              <Button variant="outline" onClick={() => { resetCreateForm(); setViewMode('list'); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Generate Form */}
      {viewMode === 'bulk' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Bulk Generate Promo Codes
            </CardTitle>
            <CardDescription>Generate multiple promo codes at once from a template. Codes are auto-generated from the description prefix.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bulk-count">Number of Codes</Label>
                <Input
                  id="bulk-count"
                  type="number"
                  placeholder="10"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(e.target.value)}
                  className="mt-1"
                  min="1"
                  max="500"
                />
                <p className="text-xs text-gray-400 mt-1">Maximum 500 codes per batch</p>
              </div>

              <div>
                <Label htmlFor="bulk-description">Description (also used as code prefix)</Label>
                <Input
                  id="bulk-description"
                  placeholder="e.g. SPRING_SALE"
                  value={bulkDescription}
                  onChange={(e) => setBulkDescription(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">First 4 characters used as code prefix</p>
              </div>

              <div>
                <Label>Discount Type</Label>
                <div className="flex items-center gap-2 mt-1">
                  {(['percentage', 'fixed', 'free_months'] as DiscountType[]).map(type => {
                    const Icon = getDiscountTypeIcon(type);
                    const labels: Record<DiscountType, string> = {
                      percentage: 'Percentage',
                      fixed: 'Fixed Amount',
                      free_months: 'Free Months'
                    };
                    return (
                      <button
                        key={type}
                        onClick={() => setBulkDiscountType(type)}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          bulkDiscountType === type
                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {labels[type]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="bulk-value">
                  {bulkDiscountType === 'percentage' ? 'Percentage (%)' :
                   bulkDiscountType === 'fixed' ? 'Amount ($)' :
                   'Number of Free Months'}
                </Label>
                <Input
                  id="bulk-value"
                  type="number"
                  placeholder={bulkDiscountType === 'percentage' ? '10' : bulkDiscountType === 'fixed' ? '5.00' : '1'}
                  value={bulkDiscountValue}
                  onChange={(e) => setBulkDiscountValue(e.target.value)}
                  className="mt-1"
                  min="0"
                  max={bulkDiscountType === 'percentage' ? '100' : undefined}
                  step={bulkDiscountType === 'fixed' ? '0.01' : '1'}
                />
              </div>

              <div>
                <Label htmlFor="bulk-max-uses">Max Uses Per Code (leave empty for unlimited)</Label>
                <Input
                  id="bulk-max-uses"
                  type="number"
                  placeholder="1"
                  value={bulkMaxUses}
                  onChange={(e) => setBulkMaxUses(e.target.value)}
                  className="mt-1"
                  min="1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="bulk-valid-from">Valid From</Label>
                  <Input
                    id="bulk-valid-from"
                    type="date"
                    value={bulkValidFrom}
                    onChange={(e) => setBulkValidFrom(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bulk-valid-until">Valid Until</Label>
                  <Input
                    id="bulk-valid-until"
                    type="date"
                    value={bulkValidUntil}
                    onChange={(e) => setBulkValidUntil(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6">
              <Button onClick={handleBulkGenerate} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                {saving ? 'Generating...' : `Generate ${bulkCount || '0'} Codes`}
              </Button>
              <Button variant="outline" onClick={() => { resetBulkForm(); setViewMode('list'); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Code Detail Sidebar */}
      {selectedCode && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                {selectedCode.code}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedCode(null)}>
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <Badge className={getCodeStatus(selectedCode).color}>
                  {getCodeStatus(selectedCode).label}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-gray-500">Discount</p>
                <p className="text-sm font-medium">{getDiscountLabel(selectedCode)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Usage</p>
                <p className="text-sm font-medium">
                  {selectedCode.usedCount}{selectedCode.maxUses !== null ? ` / ${selectedCode.maxUses}` : ' (unlimited)'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Description</p>
                <p className="text-sm font-medium">{selectedCode.description}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Valid From</p>
                <p className="text-sm font-medium">{formatTimestampFull(selectedCode.validFrom)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Valid Until</p>
                <p className="text-sm font-medium">{formatTimestampFull(selectedCode.validUntil)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Created</p>
                <p className="text-sm font-medium">{formatTimestampFull(selectedCode.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Created By</p>
                <p className="text-sm font-medium text-gray-600 truncate">{selectedCode.createdBy}</p>
              </div>
            </div>

            {selectedCode.active && getCodeStatus(selectedCode).label !== 'Expired' && (
              <div className="mt-4 pt-4 border-t flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyCode(selectedCode)}
                >
                  {copiedCodeId === selectedCode.id ? (
                    <><CheckCircle className="h-4 w-4 mr-1 text-green-500" /> Copied</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-1" /> Copy Code</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeactivate(selectedCode)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Deactivate
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Promo Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Promo Codes
            <Badge variant="secondary" className="ml-1">{filteredCodes.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCodes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchQuery || filterMode !== 'all' ? (
                <>
                  <Filter className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No promo codes match your filters</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => { setSearchQuery(''); setFilterMode('all'); }}
                  >
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  <Ticket className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No promo codes created yet</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setViewMode('create')}
                  >
                    Create your first promo code
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 pr-4 font-medium text-gray-500">Code</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Description</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Discount</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Usage</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Valid</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Status</th>
                    <th className="pb-3 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map(code => {
                    const status = getCodeStatus(code);
                    return (
                      <tr
                        key={code.id}
                        className={`border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                          selectedCode?.id === code.id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedCode(selectedCode?.id === code.id ? null : code)}
                      >
                        <td className="py-3 pr-4">
                          <span className="font-mono font-semibold text-blue-600">{code.code}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-gray-700 max-w-[200px] truncate block">{code.description}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="whitespace-nowrap">{getDiscountLabel(code)}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="whitespace-nowrap">
                            {code.usedCount}{code.maxUses !== null ? ` / ${code.maxUses}` : ''}
                          </span>
                          {code.maxUses !== null && code.usedCount >= code.maxUses && (
                            <span className="text-xs text-yellow-600 ml-1">(full)</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="text-xs text-gray-500">
                            <div>{formatTimestamp(code.validFrom)}</div>
                            <div className="text-gray-400">to {formatTimestamp(code.validUntil)}</div>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge className={status.color}>{status.label}</Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyCode(code)}
                              title="Copy code"
                            >
                              {copiedCodeId === code.id ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                            {code.active && getCodeStatus(code).label !== 'Expired' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeactivate(code)}
                                title="Deactivate"
                                className="text-red-500 hover:text-red-700"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
